import { TFunction } from 'i18next';
import { Cheerio, CheerioAPI } from 'cheerio';
import type { Element } from 'domhandler';
import { Namespace } from '../../../../translation/namespace';
// linter changes always back to this when saving :-(
// eslint-disable-next-line max-len
import { linkChooserFileLinkContentAsString } from '../../../../components/molecules/Form/components/Editor/plugins/Linkchooser/LinkChooserFileLink';
import { SONResourceReferencesToUrls } from '../types';
import { LinkchooserLinkType } from '../../../../types/api/LinkchooserLinkType';
import { isMailProtocol, isTelProtocol } from '../../../../components/atoms/Hyperlink/Hyperlink.utils';

function resolveHref(resourceReferencesToUrls: SONResourceReferencesToUrls, element: Element) {
  let href = resourceReferencesToUrls[element.attribs['data-resource-reference']]?.value;
  if (element.attribs['data-anchor']) {
    href = `${href || ''}#${element.attribs['data-anchor']}`;
  }

  return href;
}

function transformIntoDownloadLink(
  aTag: Cheerio<Element>,
  $: CheerioAPI,
  attributes: Record<string, string>,
  t: TFunction,
) {
  const fileExtension = attributes?.download?.includes('.') ? attributes.download.split('.').reverse()[0] : undefined;
  aTag.addClass('download__link download__link--no-margin download__link--no-padding');

  if (fileExtension) {
    aTag.attr('title', t(`${Namespace.Download}:${fileExtension.toLowerCase()}.description`));
  }
  aTag
    .contents()
    .replaceWith(
      $(
        linkChooserFileLinkContentAsString(aTag.contents().text(), fileExtension, attributes['data-file-size']),
      ).removeAttr(' data-reactroot'),
    );
}

/**
 * Surrounding the anchros with invisible informations about what is going to happen
 * with priority: _blank, mail, tel
 */
function addInvisibleOpeningInfo(aTag: Element, $: CheerioAPI, t: TFunction) {
  const { href, target } = aTag.attribs;

  let invisibleOpeningInfo = '';

  if (target && typeof target === 'string' && target.includes('_blank')) {
    invisibleOpeningInfo = t(`${Namespace.phrases}:linkOpensInNewWindowSmall`);
  } else if (isMailProtocol(href)) {
    invisibleOpeningInfo = t(`${Namespace.phrases}:linkOpensEmail`);
  } else if (isTelProtocol(href)) {
    invisibleOpeningInfo = t(`${Namespace.phrases}:linkOpensTel`);
  }
  if (invisibleOpeningInfo !== '') {
    $(`<span class="invisible"> (${invisibleOpeningInfo})</span>`).appendTo(aTag);
  }
}

export function replaceAllDownloadLinksWithA11yConformMetaData(t: TFunction) {
  return ($: CheerioAPI) => {
    $('a').each((_i, element) => {
      if (element.attribs['data-link-type'] === LinkchooserLinkType.Attachment) {
        transformIntoDownloadLink($(element), $, element.attribs, t);
      }
    });

    return $;
  };
}

/**
 * Takes the raw html content from the backend and resolves the contained dynamic resource references
 * with the provided urls. Example:
 *
 * When 'rawContent' contains:
 *        <a data-resource-reference="page/son_privacypolicy">
 * and 'resourceReferencesToToUrls' has a mapping like
 *       { 'page/son_privacypolicy': { value: '/inhalte/son/privacypolicy' } }
 * then the output should be the following:
 *      <a href="/inhalte/son/privacypolicy">
 *
 * if optional data-anchor is given:
 *      <a data-resource-reference="page/son_privacypolicy" data-anchor="#headline1">
 * then output is:
 *      <a href="/inhalte/son/privacypolicy#headline1">
 *
 * The data-linkType attribute is removed anyway, because it's only needed to check
 * which tab is opened in linkchooser
 * the referenceNotFound string is needed to identify if a reference can not be
 * found in the backend data an not leave the href empty in that case.
 */
export function resolveResourceReferences(resourceReferencesToUrls: SONResourceReferencesToUrls, t: TFunction) {
  return ($: CheerioAPI) => {
    if (!resourceReferencesToUrls) {
      return $;
    }

    $('a').each((_i, element) => {
      const tmp = $(element);

      // for backwards compatibility still checking data-breed when found in existing data
      const linkType = element.attribs['data-breed'] || element.attribs['data-link-type'];

      if (linkType === LinkchooserLinkType.External) {
        addInvisibleOpeningInfo(element, $, t);
      } else if (linkType === LinkchooserLinkType.Attachment) {
        transformIntoDownloadLink(tmp, $, element.attribs, t);
      }

      const href = resolveHref(resourceReferencesToUrls, element);

      if (href) {
        tmp.attr('href', href);
      }

      // for backwards compatibility still removing data-breed when found in existing data
      tmp.removeAttr(
        'data-breed data-link-type aria-describedby data-resource-reference data-anchor data-file-size download',
      );
    });

    return $;
  };
}
